// Copyright ©️ 2026 eVolve MEP, LLC

import type { Shipment } from '@evolvemep/api-ts';

import { GridMenu } from 'components/Mantine/GridMenu';
import { useShipmentModals } from 'modules/Shop/Shipments/modalRegistries';
import { useCanEditShipment } from 'modules/Shop/Shipments/ShipmentDetail/useCanEditShipment';
import { useShipments } from 'modules/Shop/Shipments/ShipmentsPage/useShipments';

type ShipmentGridMenuProps = {
  shipment: Shipment;
  onShipmentUpdated: () => void;
  onShipmentDeleted: () => void;
};
export const ShipmentWorkflowGridMenu = ({ shipment, onShipmentUpdated, onShipmentDeleted }: ShipmentGridMenuProps) => {
  const { openModal } = useShipmentModals();
  const { canEditShipment } = useCanEditShipment(shipment);
  const shipmentsContext = useShipments();
  const { mutationRunning } = shipmentsContext;

  return (
    <GridMenu
      variant="light"
      size="input-sm"
      options={[
        {
          label: 'Revert to draft',
          value: 'revertToDraft',
          hidden: !canEditShipment('revertToDraft'),
        },
        {
          label: 'Revert to ready to ship',
          value: 'revertToReady',
          hidden: !canEditShipment('revertToReady'),
        },
        {
          label: 'Revert to shipped',
          value: 'revertToShipped',
          hidden: !canEditShipment('revertToShipped'),
        },
        {
          label: 'Print packing slip',
          value: 'printPackingSlip',
        },
        {
          label: 'Delete',
          value: 'deleteShipment',
          hidden: !canEditShipment('delete'),
          color: 'red',
        },
      ]}
      disabled={mutationRunning}
      onMenuItemSelected={async (item) => {
        // The following two options are handled by modals; everything else maps to a workflow mutation on the context.
        if (item === 'printPackingSlip') {
          openModal('ShipmentPackingSlipModal', { shipment });
          return;
        }

        if (item === 'deleteShipment') {
          openModal('DeleteShipmentModal', { shipment, onShipmentDeleted });
          return;
        }
        const action = shipmentsContext[item];
        await action(shipment.shipmentId);
        onShipmentUpdated();
      }}
    />
  );
};
